
// composition-api
import { computed, defineComponent, reactive, ref } from "vue";

// helpers
import routeGuard from "@/helpers/routeGuard";

//tipos
import { Filtros } from "@/typings/components/monitoreo/filtros";
import { Pesaje } from "@/typings/store/plugins/easyFirestore/pesajes";
import { Cliente } from "@/typings/store/plugins/easyFirestore/clientes";
import { Servicio } from "@/typings/store/plugins/easyFirestore/servicios";

// composables
import { usePesajes } from "@/composables/pesajes";
import { useUsuario } from "@/composables/usuario";
import { useClientes } from "@/composables/clientes";
import { useServicios } from "@/composables/servicios";

// componentes
import TabsMonitoreo from "@/components/monitoreo/TabsMonitoreo.vue";
import FiltrosMonitoreo from "@/components/monitoreo/Filtros.vue";

export default defineComponent({
  name: "VistaMonitoreo",
  components: {
    TabsMonitoreo,
    FiltrosMonitoreo,
  },
  beforeRouteEnter(_to, _from, next) {
    next(routeGuard);
  },
  created(): void {
    if (!routeGuard()) {
      return;
    }
  },
  setup() {
    const _pesajes = usePesajes();
    const _usuario = useUsuario();
    const _clientes = useClientes({ ordenarArray: true });
    const _servicios = useServicios({ ordenarArray: true });

    const tab = ref(0);

    const usuario = _usuario.get;
    const clientesUsuario = computed((): Array<Cliente> => {
      if (usuario.value?.clientes && usuario.value.clientes.length > 0) {
        return usuario.value.clientes;
      }
      return usuario.value?.cliente ? [usuario.value.cliente] : [];
    });
    const serviciosUsuario = computed((): Array<Servicio> => {
      return usuario.value?.servicios ?? [];
    });

    const filtros = reactive<Filtros>({
      fechas: [],
      clientes: [],
      servicios: [],
      periodo: 1,
      unidadDesechos: "kg",
    });

    if (clientesUsuario.value.length > 0) {
      filtros.clientes.push(...clientesUsuario.value);
    }

    const arrayClientes = computed(() => {
      if (clientesUsuario.value.length > 0) return clientesUsuario.value;
      return _clientes.getArrayHabilitados.value;
    });

    const arrayServicios = computed(() => {
      if (serviciosUsuario.value.length > 0) return serviciosUsuario.value;
      return _servicios.getArray.value;
    });

    const validarCliente = (_pesaje: Pesaje, cliente: Cliente): boolean => {
      // Si el cliente del filtro no tiene ID, comparamos por nombre
      if (!cliente.id) {
        return (
          !!_pesaje.cliente?.nombre &&
          !!cliente.nombre &&
          _pesaje.cliente.nombre.toLowerCase() === cliente.nombre.toLowerCase()
        );
      }
      if (!_pesaje.cliente) return false;
      if (!_pesaje.cliente.id)
        return (
          !!_pesaje.cliente.nombre &&
          !!cliente.nombre &&
          _pesaje.cliente.nombre.toLowerCase() === cliente.nombre.toLowerCase()
        );
      return (
        _pesaje.cliente.id === cliente.id &&
        _pesaje.cliente.nombre?.toLowerCase() === cliente.nombre?.toLowerCase()
      );
    };

    const validarServicio = (_pesaje: Pesaje, servicio: Servicio): boolean => {
      // Si el servicio del filtro no tiene ID, comparamos por nombre
      if (!servicio.id) {
        return (
          !!_pesaje.servicio?.nombre &&
          !!servicio.nombre &&
          _pesaje.servicio.nombre.toLowerCase() ===
            servicio.nombre.toLowerCase()
        );
      }
      if (!_pesaje.servicio) return false;
      if (!_pesaje.servicio.id)
        return (
          !!_pesaje.servicio.nombre &&
          !!servicio.nombre &&
          _pesaje.servicio.nombre.toLowerCase() ===
            servicio.nombre.toLowerCase()
        );
      return (
        _pesaje.servicio.id === servicio.id &&
        _pesaje.servicio.nombre?.toLowerCase() ===
          servicio.nombre?.toLowerCase()
      );
    };

    const validarFecha = (
      _pesaje: Pesaje,
      _fechaInicio: Date,
      _fechaFinal: Date,
    ): boolean => {
      if (!_pesaje.entrada.fecha) return false;
      let fechaEntrada = _pesaje.entrada.fecha.toDate();
      return fechaEntrada >= _fechaInicio && fechaEntrada <= _fechaFinal;
    };

    const pesajesFiltrados = computed((): Array<Pesaje> => {
      return _pesajes.getArray.value.filter((pesaje) => {
        const filtroClientes = filtros.clientes;
        const filtroServicios = filtros.servicios;
        const [fechaInicio, fechaFin] = filtros.fechas;

        const filtroCliente =
          clientesUsuario.value.length > 0
            ? clientesUsuario.value.some((cliente) =>
                validarCliente(pesaje, cliente),
              )
            : filtroClientes.length > 0
            ? filtroClientes.some((cliente) => validarCliente(pesaje, cliente))
            : true;
        const filtroServicio =
          filtroServicios.length > 0
            ? filtroServicios.some((servicio) =>
                validarServicio(pesaje, servicio),
              )
            : true;

        let filtroFecha = true;
        if (fechaInicio && fechaFin) {
          filtroFecha = validarFecha(pesaje, fechaInicio, fechaFin);
        }

        const filtroEstado = pesaje.estado !== "anulado";
        return filtroCliente && filtroServicio && filtroFecha && filtroEstado;
      });
    });

    const fetchPesajes = (fechas: Array<Date>): void => {
      _pesajes.fetchAndAdd({
        pathVariables: {
          idEmpresa: _usuario.getEmpresa.value.id ?? "",
        },
        clauses: {
          where: [
            ["entrada.fecha", ">=", fechas[0].toISOString()],
            ["entrada.fecha", "<=", fechas[1].toISOString()],
          ],
        },
      });
    };

    return {
      filtros,
      arrayClientes,
      arrayServicios,
      tab,
      pesajesFiltrados,
      fetchPesajes,
    };
  },
});
